import { useState } from 'react';
import { signInWithRedirect } from 'aws-amplify/auth';

import { useAuthStore } from '../../../store/authStore';
import SharedButton from '@/global/Button/Button';
import BackgroundPages from '@/global/Background/BackgroundPages';
import useText from '@/hooks/useText';
import { useNavigate } from 'react-router-dom';

const registeredSsoProviders = { 'disgenet.com': 'entra-test', 'otsuka-us.com': 'otsuka' };

function Login() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const { logout } = useAuthStore();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const title = useText('home.hero.title');
  const titleHighlight = useText('home.hero.title_highlight');

  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = event.target.value.trim();
    setEmail(emailValue);
  };

  const handleCancel = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    navigate('/login', { replace: true });
  };

  const handleSso = async (evt: React.FormEvent) => {
    evt.preventDefault();
    try {
      setIsLoading(true);
      setErrorMessage('');

      if (!email) {
        throw new Error('Please enter your email address');
      }

      const domain = email.split('@')[1];
      if (Object.keys(registeredSsoProviders)?.includes(domain)) {
        const ssoProvider = registeredSsoProviders[domain];
        await signInWithRedirect({
          provider: { custom: ssoProvider },
        });
      } else {
        throw new Error(`Domain "${domain}" is not registered for SSO. Please contact support@disgenet.com`);
      }
    } catch (err) {
      const typedError = err as { message: string };
      setErrorMessage(typedError.message || 'An error occurred during SSO login');

      // cleanup session using AuthStore
      await logout();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="apply-trial-page">
      <BackgroundPages />
      <div className="apply-trial-content login-split-wrap">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading" />
          </div>
        )}
        <div className="login-split">
          <div className="login-split-copy">
            <h1 className="home-hero-title">
              {title}
              <span className="home-hero-highlight">{titleHighlight}</span>
            </h1>
            <p className="login-split-lead">
              Sign in with your organisation account to access the curated disease–gene–variant knowledge
              base.
            </p>
          </div>
          <div className="login-card-col">
            <div className="login-card">
            <h3 className="login-card-title">SSO Login</h3>
            <p className="login-card-subtitle">Enter your institutional email to continue.</p>
            <form onSubmit={handleSso}>
              <div className="text-left w-full" data-validate="Email is required">
                <label className="login-card-label" htmlFor="sso-email">
                  Email
                </label>
                <input
                  className="form-input"
                  type="email"
                  name="email"
                  id="sso-email"
                  value={email}
                  onChange={handleChangeEmail}
                  placeholder="you@institution.org"
                  required
                />
              </div>
              <div className="mt-5">
                <SharedButton type="submit" variant="primary" className="w-full">
                  Login
                </SharedButton>
              </div>
              {errorMessage ? <p className="form-error mt-3">{errorMessage}</p> : null}
            </form>
            <div className="login-card-footer">
              <a href="true" onClick={handleCancel}>
                Cancel
              </a>
            </div>
          </div>
          <p className="login-encryption-note">Protected by enterprise-grade encryption</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
